import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../services/authService';
import AuthLayout from '../components/public/AuthLayout';
import TextField from '../components/public/TextField';

const AcceptInvite: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = useMemo(() => searchParams.get('token') ?? '', [searchParams]);
  const [token, setToken] = useState(tokenFromUrl);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [pwd1, setPwd1] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (pwd1 !== pwd2) { setError('Passwords do not match.'); return; }
    if (pwd1.length < 14) { setError('Password must be at least 14 characters.'); return; }
    setLoading(true);
    try {
      await authService.acceptInvitation(token, pwd1, firstName, lastName, email || undefined);
      setDone(true);
      setTimeout(() => navigate('/login'), 1500);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
        ?? 'Unable to accept invitation.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Accept your invitation"
      subtitle="Welcome — pick a password to activate your account."
      backHref="/"
      backLabel="Back to home"
      footer={
        <Link to="/login" className="block text-[var(--text-muted)] hover:text-[var(--text)]">
          Already activated? Sign in
        </Link>
      }
    >
      {done ? (
        <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg px-4 py-3">
          Account created. Redirecting to sign in…
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
              {error}
            </div>
          )}
          {!tokenFromUrl && (
            <TextField
              label="Invitation token"
              required
              value={token}
              onChange={(e) => setToken(e.target.value)}
            />
          )}
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            helperText="Used as your sign-in. If your invitation already has an email attached, this is ignored and the invited address wins."
          />
          <div className="grid grid-cols-2 gap-3">
            <TextField label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            <TextField label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
          <TextField
            label="Password"
            type="password"
            autoComplete="new-password"
            autoFocus
            required
            value={pwd1}
            onChange={(e) => setPwd1(e.target.value)}
            helperText="At least 14 characters."
          />
          <TextField
            label="Confirm password"
            type="password"
            autoComplete="new-password"
            required
            value={pwd2}
            onChange={(e) => setPwd2(e.target.value)}
          />
          <button
            type="submit"
            disabled={loading || !token || !email || !pwd1 || !pwd2}
            className="w-full bg-[var(--primary)] text-white py-3 rounded-lg text-sm font-semibold hover:bg-[var(--primary-light)] disabled:opacity-50 transition-colors"
          >
            {loading ? 'Creating…' : 'Create my account'}
          </button>
        </form>
      )}
    </AuthLayout>
  );
};

export default AcceptInvite;
